import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPE_LABEL = { invoice_settlement: "Invoice settlement", advance: "Advance", expense: "Expense", loan: "Loan", emi: "EMI / loan repayment",
  transfer: "Transfer", refund: "Refund", bank_charges: "Bank charges", interest: "Interest", drawings: "Drawings", capital: "Capital", other: "Other" };
const TAG_DEFAULT = { bank_charges: ["Bank Charges", "Indirect Expenses", "bank_charges"], interest: ["Bank Interest Received", "Indirect Incomes", "interest"] };

function initial(t, parties) {
  const tag = (t.tags || []).find((x) => TAG_DEFAULT[x]);
  const ai = t.ai_suggestion || {};
  const p = parties.find((x) => x.id === t.party_id);
  const name = t.party_name || (tag ? TAG_DEFAULT[tag][0] : t.party_extracted || ai.party || "");
  const transfer = (t.tags || []).includes("internal_transfer");
  return {
    party_id: t.party_id || null, party_name: tag && !t.party_name ? "" : name,
    ledger: t.ledger || p?.ledger_name || (tag ? TAG_DEFAULT[tag][0] : name),
    ledger_group: p?.group || (tag ? TAG_DEFAULT[tag][1] : transfer ? "Bank Accounts" : t.direction === "credit" ? "Sundry Debtors"
      : ai.category === "expense" ? "Indirect Expenses" : "Sundry Creditors"),
    nature: t.nature || "business",
    txn_type: t.txn_type || t.suggested_txn_type || (tag ? TAG_DEFAULT[tag][2] : ai.category) || "other",
    voucher_type: t.voucher_type || t.suggested_voucher_type || (t.direction === "credit" ? "Receipt" : "Payment"),
    bill_no: t.bill_no || "", remarks: t.remarks || "", remember: true,
  };
}

const Field = ({ q, children }) => (<div className="space-y-1.5"><Label className="text-xs text-muted-foreground">{q}</Label>{children}</div>);

const Pick = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export function ClassifyForm({ txns, onDone, mode = "classify", submitLabel }) {
  const { parties, ledgers, meta, refreshMasters, accounts, can } = useApp();
  const t = txns[0];
  const [f, setF] = useState(() => initial(t, parties));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const total = useMemo(() => txns.reduce((a, x) => a + Number(x.amount), 0), [txns]);
  const pickParty = (name) => {
    const p = parties.find((x) => x.name.toLowerCase() === name.toLowerCase());
    setF((s) => ({ ...s, party_name: name, party_id: p?.id || null, ...(p ? { ledger: p.ledger_name, ledger_group: p.group, nature: p.classification || s.nature } : { ledger: name }) }));
  };
  const submit = async () => {
    if (!f.ledger || !f.voucher_type) return toast.error("Ledger and voucher type are required");
    setBusy(true);
    try {
      if (mode === "suspense") {
        if (!note.trim()) { setBusy(false); return toast.error("Add a resolution note — suspense entries are never cleared silently"); }
        await api.post(`/transactions/${t.id}/resolve-suspense`, { note, classify: f });
        toast.success("Suspense entry resolved and classified");
        onDone?.();
      } else if (txns.length > 1) {
        const r = await api.post("/transactions/bulk", { ids: txns.map((x) => x.id), action: "classify", classify: f });
        toast.success(`${r.data.done} entries classified${r.data.errors.length ? `, ${r.data.errors.length} skipped` : ""}`);
        onDone?.();
      } else {
        const r = await api.post(`/transactions/${t.id}/classify`, f);
        const sim = r.data.similar_pending_ids || [];
        toast.success("Classified — awaiting checker approval", sim.length ? {
          action: { label: `Apply to ${sim.length} similar`, onClick: async () => {
            const b = await api.post("/transactions/bulk", { ids: sim, action: "classify", classify: { ...f, party_id: r.data.party_id } });
            toast.success(`${b.data.done} similar entries classified`);
            onDone?.();
          } }, duration: 10000 } : undefined);
        onDone?.();
      }
      refreshMasters();
    } catch (e) { toast.error(await errMsg(e)); }
    setBusy(false);
  };
  const ai = t.ai_suggestion;
  const dirWord = t.direction === "credit" ? "from" : "to";
  return (
    <div className="space-y-4" data-testid="classify-form">
      {txns.length > 1 && <div className="rounded-lg bg-primary/10 px-3 py-2 text-xs text-primary">Bulk: {txns.length} entries · total {inr(total.toFixed(2))}</div>}
      {(ai || (t.party_suggestions || []).length > 0) && (
        <div className="flex flex-wrap gap-2">
          {ai?.party && (
            <button onClick={() => pickParty(ai.party.replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\B\w+/g, (w) => w.toLowerCase()))} data-testid="ai-suggestion-chip"
              className="flex items-center gap-1.5 rounded-full border border-teal-500/40 bg-teal-500/10 px-3 py-1 text-xs text-teal-700 transition-colors hover:bg-teal-500/20 dark:text-teal-300">
              <Sparkles className="h-3 w-3" /> AI: {ai.party} · {TYPE_LABEL[ai.category] || ai.category} · {Math.round((ai.confidence || 0) * 100)}%
            </button>
          )}
          {(t.party_suggestions || []).map((s) => (
            <button key={s.party_id} onClick={() => pickParty(s.name)} data-testid="similar-party-chip"
              className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-300">
              Similar to {s.name} ({Math.round(s.similarity)}%) — same party?
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field q={`Who is this payment ${dirWord}?`}>
          <Input list="party-list" value={f.party_name} onChange={(e) => pickParty(e.target.value)} placeholder="Search or type a new party" data-testid="classify-party-input" />
          <datalist id="party-list">{parties.map((p) => <option key={p.id} value={p.name} />)}</datalist>
        </Field>
        <Field q="Is this business or personal?">
          <Pick value={f.nature} onChange={set("nature")} items={[["business", "Business"], ["personal", "Personal"]]} testid="classify-nature-select" />
        </Field>
        <Field q="Which ledger should this entry belong to?">
          <Input list="ledger-list" value={f.ledger} onChange={set("ledger")} data-testid="classify-ledger-input" />
          <datalist id="ledger-list">{ledgers.map((l) => <option key={l.id} value={l.name}>{l.group}</option>)}{accounts.map((a) => <option key={a.id} value={a.tally_ledger} />)}</datalist>
        </Field>
        <Field q="Ledger group (for new ledgers)">
          <Pick value={f.ledger_group} onChange={set("ledger_group")} items={meta.groups.map((g) => [g, g])} testid="classify-group-select" />
        </Field>
        <Field q="Is this an advance, invoice settlement, expense, loan, transfer, refund or other?">
          <Pick value={f.txn_type} onChange={set("txn_type")} items={meta.txn_types.map((x) => [x, TYPE_LABEL[x] || x])} testid="classify-type-select" />
        </Field>
        <Field q="Voucher type">
          <Pick value={f.voucher_type} onChange={set("voucher_type")} items={meta.voucher_types.map((x) => [x, x])} testid="classify-voucher-select" />
        </Field>
        <Field q="Do you have a bill number?">
          <Input value={f.bill_no} onChange={set("bill_no")} placeholder="e.g. INV-1042 (optional)" data-testid="classify-bill-input" />
        </Field>
        <Field q="Remarks">
          <Input value={f.remarks} onChange={set("remarks")} data-testid="classify-remarks-input" />
        </Field>
      </div>
      {["Sales", "Purchase"].includes(f.voucher_type) && <div className="text-xs text-amber-600 dark:text-amber-400">Sales/Purchase vouchers need an underlying invoice — a bank movement alone is not evidence of a taxable sale or purchase.</div>}
      {mode === "suspense" && <Field q="Resolution note (required)"><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="suspense-resolution-note" /></Field>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={f.remember} onCheckedChange={(v) => setF((s) => ({ ...s, remember: !!v }))} data-testid="classify-remember-checkbox" />
          Remember this mapping for future statements{t.party_extracted ? ` ("${t.party_extracted}")` : ""}
        </label>
        <Button onClick={submit} disabled={busy || !can("admin", "accountant")} data-testid="classify-submit-button">{busy ? "Saving…" : submitLabel || "Confirm classification"}</Button>
      </div>
      <div className="text-[11px] text-muted-foreground">{fmtDate(t.txn_date)} · original narration preserved exactly as printed</div>
    </div>
  );
}
